import React, { useRef, useState } from "react";
import { ImagePlus, LayoutTemplate, Loader2 } from "lucide-react";
import { toast } from "react-toastify";

export function buildCertificateDesign({
  backgroundImage = "",
  template = "certificate",
  key = "certificate",
} = {}) {
  return {
    meta: {
      version: 5,
      layoutMode: "canvas",
      hidePairHeading: true,
      template,
    },
    key,
    canvas: {
      width: 800,
      height: 566,
      padding: 0,
      borderRadius: 0,
      borderColor: "#000000",
      borderWidth: 0,
      background: "#ffffff",
      backgroundImage,
      backgroundImageOpacity: 1,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      borderBottomLeftRadius: 0,
      backgroundMode: "solid",
      backgroundGradientFrom: "#ffffff",
      backgroundGradientTo: "#ffffff",
      backgroundGradientAngle: 135,
      printBackgroundImage: true,
    },
    header: {
      enabled: false,
      height: 50,
      bg: "#7e6dd7",
      text: "",
      color: "#ffffff",
      fontFamily: "Inter",
      fontWeight: 700,
      fontSize: 16,
      align: "center",
      x: 0,
      y: 0,
    },
    sectionsOrder: [
      "eventName",
      "venue",
      "ticketValidDates",
      "date",
      "attendeeName",
      "ticketName",
      "qr",
      "profile",
    ],
    sections: certificateSections(),
  };
}

function certificateSections() {
  return {
    eventName: {
      enabled: false,
      type: "text",
      label: "Event Name",
      keyPath: "eventName",
      fontFamily: "Inter",
      fontWeight: 700,
      fontSize: 26,
      color: "#111827",
      align: "center",
      x: 100,
      y: 40,
      width: 600,
      spacing: 10,
    },
    venue: {
      enabled: false,
      type: "text",
      label: "Venue",
      keyPath: "venue",
      keyPathFallbacks: ["venue", "location"],
      fontFamily: "Inter",
      fontWeight: 400,
      fontSize: 16,
      color: "#374151",
      align: "center",
      x: 100,
      y: 110,
      width: 600,
      spacing: 15,
      height: 22,
    },
    ticketValidDates: {
      enabled: false,
      type: "text",
      label: "Validity",
      keyPath: "ticketValidDates",
      fontFamily: "Inter",
      fontWeight: 400,
      fontSize: 16,
      color: "#374151",
      align: "center",
      x: 100,
      y: 150,
      width: 600,
      spacing: 15,
    },
    date: {
      enabled: false,
      type: "text",
      label: "Event Date",
      keyPath: "date",
      fontFamily: "Inter",
      fontWeight: 400,
      fontSize: 14,
      color: "#374151",
      align: "center",
      x: 100,
      y: 185,
      width: 600,
      spacing: 5,
    },
    attendeeName: {
      enabled: false,
      type: "text",
      label: "Attendee Name",
      keyPath: "name",
      fontFamily: "Inter",
      fontWeight: 700,
      fontSize: 34,
      color: "#111827",
      align: "center",
      x: 100,
      y: 250,
      width: 600,
      spacing: 20,
    },
    ticketName: {
      enabled: false,
      type: "text",
      label: "Ticket Class",
      keyPathFallbacks: ["ticketName", "ticketClassName"],
      bg: "transparent",
      textColor: "#111827",
      fontWeight: 700,
      fontSize: 20,
      height: 24,
      align: "center",
      fullWidth: true,
      x: 250,
      y: 470,
      width: 300,
      spacing: 20,
      color: "#111827",
      fontFamily: "Inter",
      verticalAlign: "middle",
    },
    qr: {
      enabled: false,
      type: "image",
      label: "QR Code",
      imageKey: "qrData",
      x: 340,
      y: 320,
      width: 120,
      height: 120,
      keepSquare: true,
      spacing: 0,
      verticalAlign: "middle",
      qrBorderColor: "#000000",
      qrBorderWidth: 0,
      qrBorderGap: 0,
    },
    profile: {
      enabled: false,
      type: "image",
      label: "Profile Photo",
      imageKey: "profile",
      x: 350,
      y: 80,
      width: 100,
      height: 100,
      keepSquare: true,
      asCircle: true,
      spacing: 0,
    },
  };
}

export default function CertificateOnboardingCards({ onPickImage, onPickEmpty }) {
  const fileRef = useRef(null);
  const [loadingImg, setLoadingImg] = useState(false);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/.test(file.type)) {
      toast.error("Please upload a PNG or JPG image");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image size should be less than 10MB");
      return;
    }
    setLoadingImg(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLoadingImg(false);
      onPickImage?.(ev.target.result);
    };
    reader.onerror = () => {
      setLoadingImg(false);
      toast.error("Could not read the image");
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="w-full max-w-2xl">
      <h2 className="text-xl font-bold text-gray-900 text-center mb-6">
        Create your certificate
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          type="button"
          disabled={loadingImg}
          onClick={() => fileRef.current?.click()}
          className="group text-left rounded-2xl border-2 border-dashed border-indigo-200 bg-white p-5 transition-all hover:border-indigo-400 hover:bg-indigo-50/40 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 mb-3">
            {loadingImg ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <ImagePlus className="w-6 h-6" />
            )}
          </div>
          <div className="text-sm font-semibold text-gray-900">
            Upload Template
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {loadingImg ? "Loading..." : "JPG or PNG as background"}
          </p>
        </button>

        <button
          type="button"
          onClick={() => onPickEmpty?.()}
          className="group text-left rounded-2xl border border-gray-200 bg-white p-5 transition-all hover:border-gray-300 hover:bg-gray-50"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 text-gray-600 mb-3">
            <LayoutTemplate className="w-6 h-6" />
          </div>
          <div className="text-sm font-semibold text-gray-900">
            Empty Canvas
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Blank certificate (800 × 566)
          </p>
        </button>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
}
